/**
 * ONE RELEASE as a folder tile — 9.3, 10.2, 11 — on CRM ▸ Releases
 * (`/crm/releases`).
 *
 * This is the level people name out loud ("we're shipping 10.2"), so it is
 * the level the page opens on: three or four folders, not a wall of four-part
 * version numbers. The tile answers the three questions asked of a release
 * without opening it — has it shipped, how many versions are in it, and which
 * is the newest — and clicking it goes to the release's own page
 * (`/crm/releases/10.2`).
 *
 * Edit and Delete sit OUTSIDE the opening button rather than inside it: a
 * button inside a button is invalid, and a stray click on "delete" while
 * meaning "open" is the kind of mistake a folder grid invites.
 */
import { Box, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Folder, Pencil, Trash2 } from "lucide-react";
import type { JSX } from "react";

import { RELEASE_STATUS_COLOR, statusLabel } from "./release-status.js";
import type { ReleaseFamily } from "./release-families.js";

interface Props {
  family: ReleaseFamily;
  canEdit: boolean;
  onOpen: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

export function ReleaseFolderCard({ family, canEdit, onOpen, onEdit, onDelete }: Props): JSX.Element {
  const { line, builds, hasLine } = family;
  const count = hasLine ? builds.length : 1;
  const newest = builds[0];

  return (
    <Box
      data-testid={`release-folder-${line.version}`}
      sx={{
        position: "relative",
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        bgcolor: "background.paper",
        transition: "border-color 120ms ease, box-shadow 120ms ease",
        "&:hover": { borderColor: "primary.main", boxShadow: 1 },
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={onOpen}
        aria-label={`Open release ${line.version}`}
        sx={{
          display: "flex",
          width: "100%",
          alignItems: "flex-start",
          gap: 1.5,
          textAlign: "left",
          border: 0,
          bgcolor: "transparent",
          px: 2,
          py: 1.75,
          pr: canEdit ? 9 : 2,
          cursor: "pointer",
          font: "inherit",
          color: "inherit",
        }}
      >
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 38,
            height: 38,
            borderRadius: 1,
            flexShrink: 0,
            color: "primary.main",
            bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
          }}
        >
          <Folder size={20} />
        </Box>
        <Box component="span" sx={{ display: "block", minWidth: 0, flex: 1 }}>
          <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Box
              component="span"
              sx={{
                fontFamily: (t) => t.typography.h6.fontFamily,
                fontSize: 20,
                fontWeight: 700,
                letterSpacing: "-0.01em",
                color: "text.primary",
              }}
            >
              {line.version}
            </Box>
            <Chip
              size="small"
              variant={line.status === "released" ? "filled" : "outlined"}
              color={RELEASE_STATUS_COLOR[line.status] ?? "default"}
              label={statusLabel(line.status)}
              sx={{ height: 20 }}
            />
            {!hasLine && (
              <Tooltip title="This version's release is not on this page — it stands on its own.">
                <Chip size="small" variant="outlined" label="Unfiled" sx={{ height: 20 }} />
              </Tooltip>
            )}
          </Box>
          <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.25 }}>
            {line.product_name ? `${line.product_name} · ` : ""}
            {count === 0 ? "No versions yet" : `${count} version${count === 1 ? "" : "s"}`}
            {newest ? ` · newest ${newest.version}` : ""}
          </Typography>
          {line.description && (
            <Typography
              sx={{
                fontSize: 12,
                color: "text.disabled",
                mt: 0.5,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {line.description}
            </Typography>
          )}
        </Box>
      </Box>

      {canEdit && (
        <Box sx={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 0.5 }}>
          <IconButton
            size="small"
            color="inherit"
            aria-label={`Edit ${line.version}`}
            onClick={onEdit}
          >
            <Pencil size={16} />
          </IconButton>
          <IconButton
            size="small"
            aria-label={`Delete ${line.version}`}
            onClick={onDelete}
            sx={{ color: "error.main" }}
          >
            <Trash2 size={16} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
}
